import React, { useState } from 'react';
import { QrCode, Camera, X, Loader2, UserCheck, Copy, Check } from 'lucide-react';
import posthog from 'posthog-js';
import { Profile, Contact } from '../../types';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../hooks/useToast';
import { QRCodeSVG } from 'qrcode.react';

interface QRManagerProps {
  mode: 'show' | 'scan';
  profile: Profile;
  onClose: () => void;
  onAddContact?: (contact: Contact) => void;
  initialAthleteId?: string;
}

export const QRManager: React.FC<QRManagerProps> = ({ mode, profile, onClose, onAddContact, initialAthleteId }) => {
  const [athleteId, setAthleteId] = useState(initialAthleteId || '');
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const { success, error } = useToast();

  const scoutPassUrl = `${window.location.origin}/scout/${profile.id}`;

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(scoutPassUrl);
    setLinkCopied(true);
    success('ScoutPass link copied!');
    setTimeout(() => setLinkCopied(false), 3000);
  };

  // Fire analytics when athlete shows their ScoutPass
  if (mode === 'show') {
    posthog.capture('scoutpass_viewed');
  }

  const handleConnect = async () => {
    const trimmedId = athleteId.trim();
    if (!trimmedId) return;

    setLoading(true);
    try {
      // 1. Fetch the athlete's profile
      const { data: athleteProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', trimmedId)
        .single();

      if (profileError || !athleteProfile) {
        error('Athlete not found. Check the ID and try again.');
        return;
      }

      // 2. Insert team_connection (UNIQUE constraint handles duplicates gracefully)
      const { error: connError } = await supabase
        .from('team_connections')
        .upsert({ coach_id: profile.id, athlete_id: trimmedId, status: 'active', added_via: 'qr_scan' });

      if (connError) {
        error('Failed to create connection. Try again.');
        return;
      }

      // 3. If this athlete already has the coach in their contacts, mark it direct
      //    (Part 5 — set is_direct_connect on the existing contact record if one exists)
      await supabase
        .from('contacts')
        .update({ is_direct_connect: true })
        .eq('user_id', trimmedId)
        .eq('school_name', profile.school);

      // 4. Notify the caller so they can refresh their roster / contact list
      if (onAddContact) {
        onAddContact({
          id: trimmedId,
          user_id: profile.id,
          name: athleteProfile.full_name,
          nickname: 'Athlete',
          role: 'Athlete',
          school_name: athleteProfile.school || '',
          status: 'New',
          is_direct_connect: true,
        } as Contact);
      }

      posthog.capture('scoutpass_scanned', { athlete_id: trimmedId });
      success(`Connected! ${athleteProfile.full_name} added to your roster.`);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-xl z-[500] flex items-center justify-center p-4">
      <div className="bg-white rounded-[3rem] w-full max-w-md overflow-hidden shadow-2xl border border-white/20">
        <div
          className={`p-6 border-b border-gray-100 flex justify-between items-center ${mode === 'show' ? 'bg-amber-600 text-white' : 'bg-gray-50'}`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-2xl ${mode === 'show' ? 'bg-white/20' : 'bg-amber-600 text-white'}`}
            >
              {mode === 'show' ? <QrCode size={22} /> : <Camera size={22} />}
            </div>
            <h3
              className={`font-black uppercase italic tracking-tight ${mode === 'show' ? 'text-white' : 'text-gray-900'}`}
            >
              {mode === 'show' ? 'My ScoutPass' : 'Connect Athlete'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className={`p-2 ${mode === 'show' ? 'text-white/50 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-8">
          {mode === 'show' ? (
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="p-4 bg-white border-[12px] border-gray-900 rounded-[2.5rem] shadow-2xl">
                <QRCodeSVG
                  value={scoutPassUrl}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                  level="M"
                />
              </div>
              <h4 className="text-2xl font-black text-gray-900 italic uppercase">
                {profile.full_name}
              </h4>
              <p className="text-sm font-bold text-amber-600 uppercase tracking-widest">
                {profile.positions?.[0]} • Class of {profile.grad_year}
              </p>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                Show this to your coach at camps & combines
              </p>
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border border-gray-200 text-slate-700 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition active:scale-95"
              >
                {linkCopied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                {linkCopied ? 'Copied!' : 'Copy ScoutPass Link'}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <UserCheck size={40} className="text-amber-600 mx-auto" />
                <p className="text-sm font-bold text-slate-700">
                  Ask the athlete to share their ScoutPass link, or paste their Profile ID below.
                </p>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  value={athleteId}
                  onChange={(e) => setAthleteId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                  placeholder="Paste athlete profile ID..."
                  className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-mono focus:ring-2 focus:ring-amber-500 outline-none"
                />
                <button
                  onClick={handleConnect}
                  disabled={!athleteId.trim() || loading}
                  className="w-full flex items-center justify-center gap-2 bg-amber-600 text-white py-4 rounded-2xl font-black uppercase italic tracking-widest text-xs hover:bg-amber-700 transition disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <UserCheck size={16} />
                  )}
                  {loading ? 'Connecting...' : 'Add to Roster'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
